import {
  DEFAULT_COMPANY_LANGUAGE,
  LanguageISO6391,
  languageISO6391Codes,
} from 'ai-constants'
import { parse } from 'cookie'

export const dashboardLanguages = languageISO6391Codes

export type DashboardLanguage = LanguageISO6391

const isDashboardLanguage = (value: unknown): value is DashboardLanguage =>
  typeof value === 'string' &&
  dashboardLanguages.some((language) => language === value)

export const resolveDashboardLanguage = (value: unknown): DashboardLanguage => {
  if (isDashboardLanguage(value)) return value

  return DEFAULT_COMPANY_LANGUAGE
}

type AcceptLanguageMatch = {
  index: number
  language: DashboardLanguage
  quality: number
}

export const getDashboardLanguageFromAcceptLanguageHeader = (
  acceptLanguageHeader?: string,
) => {
  if (!acceptLanguageHeader) return DEFAULT_COMPANY_LANGUAGE

  const bestMatch = acceptLanguageHeader
    .split(',')
    .reduce<AcceptLanguageMatch | undefined>(
      (currentMatch, acceptLanguageValue, index) => {
        const [languageTag = '', ...params] = acceptLanguageValue
          .trim()
          .toLowerCase()
          .split(';')

        if (!languageTag) return currentMatch

        const language = dashboardLanguages.find(
          (dashboardLanguage) =>
            languageTag === dashboardLanguage ||
            languageTag.startsWith(`${dashboardLanguage}-`),
        )

        if (!language) return currentMatch

        const qualityParam = params.find((param) =>
          param.trim().startsWith('q='),
        )
        const qualityValue = qualityParam?.trim().replace('q=', '')
        const parsedQuality = qualityValue ? Number(qualityValue) : 1
        const quality = Number.isFinite(parsedQuality) ? parsedQuality : 1

        if (!currentMatch) return { index, language, quality }
        if (quality > currentMatch.quality) return { index, language, quality }
        if (quality === currentMatch.quality && index < currentMatch.index)
          return { index, language, quality }

        return currentMatch
      },
      undefined,
    )

  return bestMatch?.language || DEFAULT_COMPANY_LANGUAGE
}

export const getDashboardLanguageFromCookieHeader = (cookieHeader?: string) => {
  if (!cookieHeader) return DEFAULT_COMPANY_LANGUAGE

  const cookies = parse(cookieHeader)

  return resolveDashboardLanguage(cookies.NEXT_LOCALE)
}

export const getDashboardLanguageFromDocument = () => {
  if (typeof document === 'undefined') return DEFAULT_COMPANY_LANGUAGE

  return getDashboardLanguageFromCookieHeader(document.cookie)
}
